import OrderDTO from "@/dto/order/OrderDTO";
import { getAddressWarning } from "@/services/api/setting/addressWarning";
import { getUndeliverableAreaList, UndeliverableArea } from "@/services/api/setting/unDeliverableArea";
import { getUndeliverableAreaCodes, UndeliverableAreaCode } from "@/services/undeliverableAreaService";
import { computed, observable, makeObservable, actionBound } from "mobx";
import AutoLockAction from "@/enum/AutoLockAction";
import { isAbnormalAddress } from "@/utils/OrderUtils";
import OrderStatus from "@/enum/OrderStatus";

export default class OrderSettingStore {
    constructor() {
        makeObservable(this, {
            loading: observable,
            init: actionBound,
            addressFilterKeywords: observable,
            abnormalAddressAction: observable,
            undeliverableAreaList: observable,
            undeliverableAreaCodes: observable,
            undeliverableAreaCodeMapping: computed,
            loadAddressWarning: actionBound,
            loadUndeliverableAreaList: actionBound,
        });
    }

    loading = false;

    async init() {
        this.loading = true;

        try {
            await Promise.all([this.loadAddressWarning(), this.loadUndeliverableAreaList()]);
        } catch (error) {
            console.error(error);
        } finally {
            this.loading = false;
        }
    }

    addressFilterKeywords: string[] = [];

    abnormalAddressAction: AutoLockAction = undefined;

    /**
     * 不可达地区配置列表
     */
    undeliverableAreaList: UndeliverableArea[] = [];

    /**
     * 不可达地区codes
     */
    undeliverableAreaCodes: UndeliverableAreaCode[] = [];

    /**
     * 不可达地区codes mapping
     */
    get undeliverableAreaCodeMapping() {
        const map = new Map<number, UndeliverableAreaCode>();

        for (let i = 0; i < this.undeliverableAreaCodes.length; i++) {
            const undeliverableAreaCode = this.undeliverableAreaCodes[i];

            map.set(undeliverableAreaCode.originalData.id, undeliverableAreaCode);
        }

        return map;
    }

    async loadAddressWarning() {
        const addressWarning = await getAddressWarning();

        if (!addressWarning?.enable) {
            this.addressFilterKeywords = [];
            this.abnormalAddressAction = undefined;
        } else {
            if (addressWarning?.keywords) {
                this.addressFilterKeywords = addressWarning?.keywords
                    ?.trim()
                    ?.split("\n")
                    ?.map((it) => it?.trim());
            }
            this.abnormalAddressAction = addressWarning?.autoAction;
        }
    }

    async loadUndeliverableAreaList() {
        const areaList = await getUndeliverableAreaList();

        this.undeliverableAreaList = observable(areaList, { deep: false });
        this.undeliverableAreaCodes = observable(getUndeliverableAreaCodes(areaList), { deep: false });
    }

    /** 快递不可达锁单原因 */
    getOrderAddressUndeliverableLockReason = (order: OrderDTO) => {
        if (order.status !== OrderStatus.PENDING_SHIPMENT) return null;

        if (this.undeliverableAreaList.length === 0) return null;

        for (let i = 0; i < this.undeliverableAreaList.length; i++) {
            const undeliverableArea = this.undeliverableAreaList[i];

            if (undeliverableArea.autoAction !== AutoLockAction.LOCK_ORDER) continue;

            const undeliverableAreaCode = this.undeliverableAreaCodeMapping.get(undeliverableArea.id);

            if (undeliverableAreaCode) {
                const reason = undeliverableAreaCode.checkUndeliverable(order);

                if (reason) return reason;
            }
        }

        return null;
    };

    /** 地址异常锁单 */
    isOrderAddressAbnormalLock = (order: OrderDTO) => {
        if (order.status !== OrderStatus.PENDING_SHIPMENT) return false;

        if (this.abnormalAddressAction !== AutoLockAction.LOCK_ORDER) return false;

        return isAbnormalAddress(order, this.addressFilterKeywords);
    };
}
